/**
 * Clinical Significance of Variant — Value Normalization
 *
 * Maps backend raw values into standardized categories and provides
 * helpers to normalize dropdown data and expand filters for API calls.
 */

// Old value → Normalized value
const CLINICAL_SIGNIFICANCE_MAP = Object.freeze({
    "Pathogenic": "Pathogenic",
    "Likely Pathogenic": "Likely Pathogenic",
    "VUS": "VUS",
    "Variants of unknown clinical significance": "VUS",
    "Drug response": "Drug response",
    "Variants of potential clinical significance": "Pathogenic",
    "Variants of strong clinical significance": "Pathogenic",
    "Not Classified": "Not Classified",
    "NA": "NA",
    "Benign": "Benign",
});

// Canonical display order for all dashboards
const CLINICAL_SIGNIFICANCE_ORDER = [
    "Pathogenic",
    "Likely Pathogenic",
    "VUS",
    "Drug response",
    "Not Classified",
    "NA",
    "Benign",
];

/**
 * Normalize raw clinical significance data from the API.
 *
 * Takes [{name, value}] from TransformRawObjectData, groups by normalized name,
 * sums counts, and attaches originalNames so filters can expand back.
 *
 * @param {Array<{name: string, value: number}>} rawItems
 * @returns {Array<{name: string, value: number, originalNames: string[]}>}
 */
export function normalizeClinicalSignificanceData(rawItems) {
    if (!rawItems || !Array.isArray(rawItems)) return [];

    const grouped = {};

    rawItems.forEach((item) => {
        const normalized = CLINICAL_SIGNIFICANCE_MAP[item.name] || item.name;

        if (!grouped[normalized]) {
            grouped[normalized] = { name: normalized, value: 0, originalNames: [] };
        }

        grouped[normalized].value += item.value || 0;

        if (!grouped[normalized].originalNames.includes(item.name)) {
            grouped[normalized].originalNames.push(item.name);
        }
    });

    const items = Object.values(grouped);

    // Sort by canonical order; unknown categories go to the end
    items.sort((a, b) => {
        const idxA = CLINICAL_SIGNIFICANCE_ORDER.indexOf(a.name);
        const idxB = CLINICAL_SIGNIFICANCE_ORDER.indexOf(b.name);
        const rankA = idxA === -1 ? Infinity : idxA;
        const rankB = idxB === -1 ? Infinity : idxB;
        return rankA - rankB;
    });

    return items;
}

/**
 * Expand selected normalized filter items back to all original backend values.
 *
 * When a user selects "Pathogenic" (combined), this returns
 * ["Pathogenic", "Variants of potential clinical significance",
 *  "Variants of strong clinical significance"] so the backend can find all records.
 *
 * @param {Array<{name: string, originalNames?: string[]}>} filterItems
 * @returns {string[]} — flat array of original names for the API
 */
export function expandClinicalSignificanceFilter(filterItems) {
    if (!filterItems || !Array.isArray(filterItems) || filterItems.length === 0) {
        return null;
    }

    const expanded = [];

    filterItems.forEach((item) => {
        if (item.originalNames && item.originalNames.length > 0) {
            item.originalNames.forEach((name) => {
                if (!expanded.includes(name)) {
                    expanded.push(name);
                }
            });
        } else {
            // if no originalNames stored, send the name as-is
            if (!expanded.includes(item.name)) {
                expanded.push(item.name);
            }
        }
    });

    return expanded.length > 0 ? expanded : null;
}
